import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { StyledTypography } from "@insite/mobius/Link";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import { contentWidthStyles } from "@store/theme/common/mixins";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    footerWrapper?: InjectableCss;
    topWideContainer?: GridContainerProps;
    bottomWideContainer?: GridContainerProps;
    middleWideContainer?: GridContainerProps;
    topWrapper?: InjectableCss;
    middleWrapper?: InjectableCss;
    bottomWrapper?: InjectableCss;
    footerLogoGridItem?: GridItemProps;
    footerSeparatorGridItem?: GridItemProps;
    footerSeparator?: InjectableCss;
    footerDetailsGridItem?: GridItemProps;
    footerLinksGridItem?: GridItemProps;
    linkListGridItem?: GridItemProps;
    footerLegalGridItem?: GridItemProps;
    footerInfoGridItem?: GridItemProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    footerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    topWideContainer: {
        gap: 0,
        css: css`
            background-color: ${getColor("secondary")};

            ${StyledTypography} {
                font-weight: ${TYPOGRAPHY.weight.medium};
                font-size: ${TYPOGRAPHY.size.smaller};
                color: ${COLORS.white};
            }

            a:hover {
                text-decoration: underline;

                ${StyledTypography} {
                    color: ${COLORS.white};
                }
            }

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["2", null, "1", null, null], properties: ["order"] }],
                    mediaOptions.min,
                )}
        `,
    },
    middleWideContainer: {
        gap: 0,
        css: css`
            background-color: ${COLORS.black};
            border-bottom: 1px solid ${COLORS.border.almostDark};

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["1", null, "2", null, null], properties: ["order"] }],
                    mediaOptions.min,
                )}
        `,
    },
    bottomWideContainer: {
        gap: 0,
        css: css`
            order: 3;
            background-color: ${COLORS.black};
        `,
    },
    topWrapper: {
        css: css`
            ${contentWidthStyles()}
            width: 100%;
            display: flex;
            flex-wrap: inherit;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["30px 30px 0", null, "10px 20px", null, null], properties: ["padding"] }],
                    mediaOptions.min,
                )}

            [class^="Wrapper"] > div {
                display: flex;
                justify-content: space-between;

                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["column", null, "row", null, null], properties: ["flex-direction"] }],
                        mediaOptions.min,
                    )}

                > div {
                    padding: 0;
                    display: flex;
                    align-items: center;

                    [class^="StyledButton"] {
                        ${({ theme }) =>
                            breakpointValuesMediaQueries(
                                theme,
                                [
                                    { values: ["16px", null, "14px", null, null], properties: ["font-size"] },
                                    { values: ["30px", null, "0", null, null], properties: ["margin-bottom"] },
                                ],
                                mediaOptions.min,
                            )}
                    }
                }
            }
        `,
    },
    middleWrapper: {
        css: css`
            ${contentWidthStyles()}
            width: 100%;
            display: flex;
            flex-wrap: inherit;
            line-height: 1.7;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["30px", null, "35px 20px 25px", null, null], properties: ["padding"] }],
                    mediaOptions.min,
                )}

            p {
                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["20px !important", null, "0 !important", null, null], properties: ["margin-top"] }],
                        mediaOptions.min,
                    )}
            }
        `,
    },
    bottomWrapper: {
        css: css`
            ${contentWidthStyles()}
            width: 100%;
            display: flex;
            flex-wrap: inherit;

            ${({ theme }) =>
                breakpointValuesMediaQueries(
                    theme,
                    [{ values: ["15px 20px", null, "15px 20px", null, null], properties: ["padding"] }],
                    mediaOptions.min,
                )}
        `,
    },
    footerLogoGridItem: {
        width: [12, 12, 3, 2, 2],
    },
    footerInfoGridItem: {
        width: [12, 12, 9, 10, 10],
        css: css`
            color: ${COLORS.text.thelightest};
        `,
    },
    footerLegalGridItem: {
        width: 12,
        align: "middle",
        css: css`
            color: ${COLORS.text.thelightest};
            text-align: center;

            p {
                margin-bottom: 0;
                padding: 0;
                opacity: 0.7;
            }
        `,
    },
};

const StoreFooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.footerWrapper}>
            <GridContainer {...styles.topWideContainer}>
                <StyledWrapper {...styles.topWrapper}>
                    <Zone zoneName="LinkList1" contentId={id} />
                </StyledWrapper>
            </GridContainer>
            <GridContainer {...styles.middleWideContainer}>
                <StyledWrapper {...styles.middleWrapper}>
                    <GridItem {...styles.footerLogoGridItem}>
                        <Zone zoneName="FooterLogo" contentId={id} />
                    </GridItem>
                    <GridItem {...styles.footerInfoGridItem}>
                        <Zone zoneName="FooterInfo" contentId={id} />
                    </GridItem>
                </StyledWrapper>
            </GridContainer>
            <GridContainer {...styles.bottomWideContainer}>
                <StyledWrapper {...styles.bottomWrapper}>
                    <GridItem {...styles.footerLegalGridItem}>
                        <Zone zoneName="FooterLegal" contentId={id} />
                    </GridItem>
                </StyledWrapper>
            </GridContainer>
        </StyledWrapper>
    );
};

const storeFooterContainer: WidgetModule = {
    component: StoreFooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        isSystem: true,
    },
};

export default storeFooterContainer;
